import type { CurrentUserDto } from "./authApi";

export interface JwtIdentity {
  id: string;
  username: string;
  email: string;
  role: string;
  accountId?: string;
  roleId?: string;
  /** Set only when this user is scoped to one specific branch — null means
   * account-wide access (they must choose a branch wherever one is needed). */
  branchId?: string | null;
}

/**
 * Builds the app's identity shape from GET /auth/me's UserDto. Confirmed
 * 2026-07-25 by reading the backend source directly (`JwtTokenGenerator.cs`):
 * the JWT itself only ever carries `sub`/`unique_name`/`email`/`role` claims
 * and NEVER an `accountId` claim, for any user — so decoding the token can
 * never recover `accountId`, and `/auth/me` (which reads the full User row
 * server-side from the token's `sub`) is the only reliable source for it.
 * See docs/api-integration.md.
 */
export const identityFromCurrentUser = (dto: CurrentUserDto): JwtIdentity => ({
  id: String(dto.userId),
  username: dto.userName,
  email: dto.email || "",
  role: dto.roleName || dto.roleCode || "user",
  accountId: String(dto.accountId),
  roleId: String(dto.roleId),
  branchId: dto.branchId === null ? null : String(dto.branchId),
});

/** Platform-level ("SuperAdmin") vs. per-tenant staff — gates Control Center access. */
export const isSuperAdminIdentity = (identity: JwtIdentity): boolean =>
  identity.role.trim().toLowerCase() === "superadmin" || identity.accountId === "0";
